import { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Checkbox,
  Chip,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import request from "../../config/request.ts";

interface AutocompleteFieldProps {
  name: string;
  label: string;
  formik?: any;
  apiUrl?: string;
  apiMethod?: "get" | "post";
  apiPayload?: any;
  options?: any[];
  value?: any[];
  optionLabelKey?: string;
  optionValueKey?: string;
  onChange?: (value: any[]) => void;
  onchangeValue?: (value: any) => void;
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function AutoCompleteMultiSelect({
  name,
  label,
  formik,
  apiUrl,
  apiMethod = "post",
  apiPayload = {},
  options: staticOptions,
  value,
  optionLabelKey = "name",
  optionValueKey = "id",
  onChange,
  onchangeValue,
}: AutocompleteFieldProps) {
  const [options, setOptions] = useState<any[]>([]);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : selectedItems;

  // Fetch options
  useEffect(() => {
    if (staticOptions) {
      setOptions(staticOptions);
      return;
    }

    if (!apiUrl) return;

    const fetchOptions = async () => {
      setLoading(true);
      try {
        const params = {
          search: inputValue,
          limit: 25,
          offset: 0,
          orderColumn: "sortOrder",
          orderDirection: "ASC",
          ...apiPayload,
        };
        const response = apiMethod === "get"
          ? await request.get(apiUrl, params)
          : await request.post(apiUrl, params);

        const apiData = response.data.data || [];

        // merge with selected items to prevent disappearing
        const merged = [...currentValue];

        apiData.forEach((item: any) => {
          if (!merged.some((m) => m[optionValueKey] === item[optionValueKey])) {
            merged.push(item);
          }
        });

        setOptions(merged);
      } catch (error) {
        console.error("Error fetching options:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, [inputValue, apiUrl, apiMethod, staticOptions]);

  // Edit mode support
  useEffect(() => {
    if (!isControlled && formik?.values?.[name]?.length) {
      setSelectedItems(formik.values[name]);
    }
  }, [formik?.values?.[name], isControlled]);

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      size="small"
      fullWidth
      limitTags={2}
      options={options}
      value={currentValue}
      loading={loading}
      filterOptions={(x) => x}
      isOptionEqualToValue={(option, value) =>
        option[optionValueKey] === value[optionValueKey]
      }
      getOptionLabel={(option) => option[optionLabelKey] || ""}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === "input") {
          setInputValue(newInputValue);
        }
      }}
      onChange={(event, newValue) => {
        if (!isControlled) {
          setSelectedItems(newValue);
        }

        // store id + label in formik
        const formatted = newValue.map((item) => ({
          [optionValueKey]: item[optionValueKey],
          [optionLabelKey]: item[optionLabelKey],
        }));

        formik?.setFieldValue(name, formatted);

        onChange?.(newValue);
        onchangeValue?.(newValue);
      }}
      renderTags={(value, getTagProps) => {
        const visible = value.slice(0, 2);
        const extra = value.length - 2;

        return (
          <>
            {visible.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option[optionValueKey]}
                label={option[optionLabelKey]}
                size="small"
              />
            ))}

            {extra > 0 && (
              <Chip label={`+${extra} more`} size="small" color="primary" />
            )}
          </>
        );
      }}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            checked={selected}
            sx={{ mr: 1 }}
          />
          {option[optionLabelKey]}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={formik?.touched?.[name] && Boolean(formik?.errors?.[name])}
          helperText={formik?.touched?.[name] && formik?.errors?.[name]}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
